import React, { useCallback } from 'react';

import { useIntl } from 'react-intl-next';

import Button, { type ButtonProps } from '@atlaskit/button/new';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import {
	AgentDropdownMenu as AgentDropdownMenuBase,
	ChatPillIcon,
} from '@atlaskit/rovo-agent-components';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/src';

import { useAgentPermissions } from '../../../../controllers/agent-permissions';

import messages from './messages';

const chatToAgentButtonContainer = xcss({
	width: '100%',
});

const chatToAgentButtonWrapper = xcss({
	display: 'flex',
	justifyContent: 'center',
	lineHeight: '20px',
	fontWeight: '500',
});

const chatPillButtonInlineStyles = xcss({ paddingInline: 'space.025' });

const chatPillTextStyles = xcss({
	wordBreak: 'break-word',
	textAlign: 'left',
	whiteSpace: 'pre-wrap',
});

const chatPillIconWrapper = xcss({
	minWidth: '20px',
	height: '20px',
});

type ChatToAgentButtonProps = {
	onClick: ButtonProps['onClick'];
};

export const ChatToAgentButton = ({ onClick }: ChatToAgentButtonProps) => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={chatToAgentButtonContainer}>
			<Button testId="view-agent-modal-chat-to-agent-button" shouldFitContainer onClick={onClick}>
				<Box xcss={chatToAgentButtonWrapper}>
					<Inline space="space.050" xcss={chatPillButtonInlineStyles}>
						<Box xcss={chatPillIconWrapper}>
							<ChatPillIcon />
						</Box>
						<Box xcss={chatPillTextStyles}>{formatMessage(messages.chatToAgentButton)}</Box>
					</Inline>
				</Box>
			</Button>
		</Box>
	);
};

type AgentDropdownMenuProps = React.ComponentPropsWithoutRef<typeof AgentDropdownMenuBase> &
	AssistanceServiceInputParam;

export const AgentDropdownMenu = (props: AgentDropdownMenuProps) => {
	const { refetch } = useAgentPermissions({
		agentId: props.agentId,
		enabled: false,
		assistanceServiceParams: props.assistanceServiceParams,
	});

	const loadAgentPermissions = useCallback(async () => {
		const response = await refetch();

		return {
			isEditEnabled: response?.permissions.AGENT_UPDATE?.permitted ?? false,
			isDeleteEnabled: response?.permissions.AGENT_DEACTIVATE?.permitted ?? false,
		};
	}, [refetch]);

	return <AgentDropdownMenuBase {...props} loadAgentPermissions={loadAgentPermissions} />;
};
